import { useEffect } from "react";

/**
 * #919 session-telemetry beacon — fires ONCE per app session (sessionStorage
 * latch) after the authed shell mounts. Fail-silent by contract: a lost or
 * rejected beacon must never affect the app, so every path swallows.
 * What's sent: device class (by viewport), viewport/screen size, pixel ratio,
 * coarse browser + OS. No location, no per-page tracking — disclosed on /guide.
 */

const LATCH = "crosbe-telemetry-sent";

function deviceClass(w: number): "phone" | "tablet" | "desktop" {
  if (w < 640) return "phone"; // Tailwind sm — matches the mobile-landing breakpoint
  if (w < 1024) return "tablet"; // lg
  return "desktop";
}

/** Coarse UA parse — family + OS only, deliberately not a fingerprint. */
export function parseUa(ua: string): { browser: string; os: string } {
  const browser = /edg\//i.test(ua)
    ? "Edge"
    : /chrome|crios/i.test(ua)
      ? "Chrome"
      : /firefox|fxios/i.test(ua)
        ? "Firefox"
        : /safari/i.test(ua)
          ? "Safari"
          : "other";
  const os = /iphone|ipad|ios/i.test(ua)
    ? "iOS"
    : /android/i.test(ua)
      ? "Android"
      : /mac os/i.test(ua)
        ? "macOS"
        : /windows/i.test(ua)
          ? "Windows"
          : /linux/i.test(ua)
            ? "Linux"
            : "other";
  return { browser, os };
}

export function useTelemetryBeacon(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;
    try {
      if (sessionStorage.getItem(LATCH)) return;
      sessionStorage.setItem(LATCH, "1");
      const { browser, os } = parseUa(navigator.userAgent);
      void fetch("/api/telemetry/session", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          device_class: deviceClass(window.innerWidth),
          viewport_w: window.innerWidth,
          viewport_h: window.innerHeight,
          screen_w: window.screen?.width,
          screen_h: window.screen?.height,
          dpr: window.devicePixelRatio,
          browser,
          os,
        }),
      }).catch(() => {});
    } catch {
      // sessionStorage unavailable (private-mode edge cases) — skip silently.
    }
  }, [enabled]);
}
